import moment from 'moment';

import { PaymentPlan } from '@dill/dill-shared';

export const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

const ordinal = (n: number): string => {
  const remainder100 = n % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

/** e.g. "Every 2 weeks on Wednesdays", "Monthly on the 15th" */
export const formatPaymentPlanOccurrence = (
  plan: Pick<PaymentPlan, 'occurrenceUnit' | 'occurrenceInterval' | 'dayOfWeek' | 'dayOfMonth'>
): string => {
  if (plan.occurrenceUnit === 'WEEK') {
    const dayLabel = `${WEEKDAYS[plan.dayOfWeek ?? 0]}s`;
    return plan.occurrenceInterval <= 1
      ? `Weekly on ${dayLabel}`
      : `Every ${plan.occurrenceInterval} weeks on ${dayLabel}`;
  }
  const dayLabel = ordinal(plan.dayOfMonth ?? 1);
  return plan.occurrenceInterval <= 1
    ? `Monthly on the ${dayLabel}`
    : `Every ${plan.occurrenceInterval} months on the ${dayLabel}`;
};

/** "Sep 21, 2026 at 3:45 PM" - the moment a buyer's confirmation click was recorded. */
export const formatPaymentPlanConfirmationTimestamp = (confirmedAt: Date | string): string =>
  moment(confirmedAt).format('MMM DD, YYYY [at] h:mm A');

export interface PaymentPlanInstallment {
  installmentNumber: number;
  date: Date;
  amount: number;
}

/**
 * Projects the remaining installments for a plan, starting at its next due date, by repeatedly
 * stepping the occurrence interval and charging amountPerPayment until the balance is covered.
 * This is a client-side projection for display only - the server is the source of truth for what
 * actually gets charged (e.g. if a cycle is missed or the plan is edited, this won't reflect it).
 */
export const generatePaymentPlanInstallments = (
  plan: Pick<
    PaymentPlan,
    | 'occurrenceUnit'
    | 'occurrenceInterval'
    | 'nextDueDate'
    | 'amountPerPayment'
    | 'remainingBalance'
  >
): PaymentPlanInstallment[] => {
  const { amountPerPayment, remainingBalance, occurrenceUnit, occurrenceInterval } = plan;
  if (!plan.nextDueDate || amountPerPayment <= 0 || remainingBalance <= 0) return [];

  const installments: PaymentPlanInstallment[] = [];
  let balanceLeft = remainingBalance;
  let date = moment(plan.nextDueDate);
  let installmentNumber = 1;

  while (balanceLeft > 0.005) {
    const amount = Math.min(amountPerPayment, balanceLeft);
    installments.push({ installmentNumber, date: date.toDate(), amount });
    balanceLeft = Math.round((balanceLeft - amount) * 100) / 100;
    date =
      occurrenceUnit === 'WEEK'
        ? date.clone().add(occurrenceInterval, 'weeks')
        : date.clone().add(occurrenceInterval, 'months');
    installmentNumber += 1;
  }

  return installments;
};
